import React from "react";
import Papa from "papaparse";
import { Button, Tooltip, Typography, Grid, Alert } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { csvPostprocessor } from "pages/dashboard/admin/import/interest_imports/components/utils";
import { ImportListMutation } from "pages/dashboard/admin/import/interest_imports/queries";
import { ImportListCSVMutation, ImportListCSVMutationVariables } from "types/gql-types";
import { LoadingSpinner } from "components/graphql-views";
import { StepProps } from "..";
import { getListName, useStepData } from "../context";
import StepLayout from "./layout";
import { validateStep } from "./validation";

// eslint-disable-next-line max-lines-per-function
const StepFinalize: React.FC<StepProps> = () => {
  const navigate = useNavigate();
  const [importList, importListResults] = useMutation<
  ImportListCSVMutation,
  ImportListCSVMutationVariables
  >(ImportListMutation);

  const { viewer, stepData, setStepName, importErrors, setImportErrors } = useStepData();
  const { listId } = stepData;

  const handleReset = () => {
    navigate("/dash/manager/settings/imports/upload?reset=true");
  };

  const handleSubmit = async () => {
    if (!stepData.listId) {
      throw new Error("unexpectedly missing listId");
    }
    if (stepData.file === null) {
      throw new Error("unexpectedly missing file object");
    }

    const processed = csvPostprocessor(stepData);
    const serialized = Papa.unparse(processed.data);

    const variables = {
      id: stepData.listId,
      csv: serialized,
      csvFileName: stepData.file?.name ?? null,
    };

    const result = await importList({ variables });
    if (result.errors) {
      console.error("Import failed", result.errors);
    } else {
      // encode total number of records in the URL
      const totalRecords = processed.data.length;
      const encodedRecords = encodeURIComponent(totalRecords.toString());
      const encodedListName = encodeURIComponent(
        getListName(viewer.lists, stepData.listId),
      );
      const queued = result.data?.importInterestList?.queued === true;
      const baseSuccessPath = "/dash/manager/settings/imports/success";
      const successUrl =
        `${baseSuccessPath}?records=${encodedRecords}&list=${encodedListName}${
          queued ? "&queued=1" : ""
        }`;
      navigate(successUrl, { replace: true });
    }
    return null;
  };

  if (!validateStep(stepData, "finalize")) {
    return null;
  }

  if (!viewer.lists) return null;

  const listName = viewer.lists.find(list => list.id === listId)?.name;

  const linkToSettings = `/dash/manager/settings/property/${listId}/lists`;

  React.useEffect(() => {
    setStepName("Finalize");
  }, []);

  React.useEffect(
    () => {
      if (importListResults.error) {
        setImportErrors(importListResults.error.graphQLErrors);
      } else if (importListResults.data) {
        setImportErrors([]);
      }
    }
    , [importListResults.error],
  );

  // Reset error when data changes
  React.useEffect(() => {
    if (stepData) setImportErrors([]);
  }, [stepData]);

  return <StepLayout
    title="Review and finalize your import"
    description={
      <Typography variant="body1">
        {stepData.data.length} applications will be added to {listName}.
      </Typography>}
  >
    <Grid size={{ xs: 12 }}>
      <ul>
        <li><Typography variant="body1"><strong>Imported from:</strong> {stepData.file.name}</Typography></li>
        <li><Typography variant="body1"><strong>New applications:</strong> {stepData.data.length}</Typography></li>
        <li><Typography variant="body1"><strong>Destination List:</strong> {listName}</Typography></li>
        <li><Typography variant="body1"><strong>Date/Time Order:</strong> {stepData.validateOrder ? "Yes" : "No"}</Typography></li>
        <li><Typography variant="body1">
          <Tooltip title="Edit Time Zone in Settings">
            <strong>Time Zone of applications:</strong>
          </Tooltip> {stepData.timezone ? <>
            {" "}{stepData.timezone}<Link to={linkToSettings}><InfoIcon fontSize="small" viewBox="0 0 30 30" /></Link>
          </> : <>
            <Alert severity="warning">
              Timezone not set for this list.
            </Alert>
            <Link to={linkToSettings}>Set Timezone</Link>
          </>}
        </Typography></li>
      </ul>
    </Grid>
    <Grid size={{ xs: 12 }}>
      <Grid container spacing={2}>
        <Grid>
          <Button variant="outlined" color="secondary" onClick={handleReset}>
            Cancel Import
          </Button>
        </Grid>
        <Grid>
          <Button
            data-cy="complete-import-button"
            variant="contained" color="primary" onClick={handleSubmit} disabled={importListResults.loading}>
            Import
          </Button>
        </Grid>
        {importListResults.loading && (
          <LoadingSpinner />
        )}
        <Grid size={{ xs: 12 }}>
          {importErrors.length > 0 && (
            <Typography color="error">
              {importErrors.map((error) => {
                // @ts-ignore
                if (error.details) {
                  // @ts-ignore
                  return Object.entries(error.details).map(([key, value]) => <>{Number.isInteger(error.row) ? `Row ${error.row}: ` : ""}{key}: {JSON.stringify(value)}<br/></>);
                }
                return null;
              })}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  </StepLayout>;
};

export default StepFinalize;
